import { Component } from '@theme/component';
import { debounce, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Desktop hover-intent timings. */
const MENU_OPEN_DELAY_MS = 300;
const MENU_SWITCH_DELAY_MS = 280;
const MENU_CLOSE_DELAY_MS = 320;
const HOVER_COMMIT_DELAY_MS = 60;
const VIEWPORT_EXIT_CLOSE_DELAY_MS = 120;


/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #activationTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #deactivationTimer;

  /** @type {HTMLElement | null} */
  #pendingItem = null;

  /** @type {ResizeObserver | null} */
  #submenuResizeObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #viewportExitTimer;

  connectedCallback() {
    super.connectedCallback();

    window.addEventListener('resize', this.#resizeListener);
    window.addEventListener('blur', this.#forceCloseFromExternalExit);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('visibilitychange', this.#onVisibilityChange);
    document.documentElement.addEventListener('pointerleave', this.#onViewportPointerLeave);
    document.documentElement.addEventListener('pointerenter', this.#onViewportPointerEnter);
    document.addEventListener('pointermove', this.#onClosedMenuPointerMove, { passive: true });
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    window.removeEventListener('blur', this.#forceCloseFromExternalExit);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('visibilitychange', this.#onVisibilityChange);
    document.documentElement.removeEventListener('pointerleave', this.#onViewportPointerLeave);
    document.documentElement.removeEventListener('pointerenter', this.#onViewportPointerEnter);
    document.removeEventListener('pointermove', this.#onClosedMenuPointerMove);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();
    this.#clearActivationTimer();
    this.#clearDeactivationTimer();
    clearTimeout(this.#viewportExitTimer);
    this.#viewportExitTimer = undefined;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
    this.#syncActiveMenuHeight();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#scheduleDeactivation();
  };


  /** Close the menu when the browser window loses focus. */
  #forceCloseFromExternalExit = () => {
    this.#clearActivationTimer();
    clearTimeout(this.#viewportExitTimer);
    this.#viewportExitTimer = undefined;
    this.#deactivate(this.#state.activeItem, true);
  };

  /** Close immediately when the tab becomes hidden. */
  #onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      this.#forceCloseFromExternalExit();
    }
  };

  /**
   * Pointer leaving the browser viewport does not always trigger the menu item's
   * pointerleave (for example when moving into browser chrome). Give it a tiny
   * grace period, then close cleanly.
   */
  #onViewportPointerLeave = (event) => {
    if (!(event instanceof PointerEvent) || event.relatedTarget !== null) return;
    this.#clearActivationTimer();
    clearTimeout(this.#viewportExitTimer);
    this.#viewportExitTimer = setTimeout(() => {
      this.#viewportExitTimer = undefined;
      this.#deactivate(this.#state.activeItem, true);
    }, VIEWPORT_EXIT_CLOSE_DELAY_MS);
  };

  #onViewportPointerEnter = () => {
    clearTimeout(this.#viewportExitTimer);
    this.#viewportExitTimer = undefined;
  };

  /**
   * Keyboard escape hatch for an open mega menu.
   * Keeps focus on the parent navigation item so keyboard users never lose
   * their position after dismissing the submenu.
   * @param {KeyboardEvent} event
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (event.isComposing || event.defaultPrevented) return;

    const activeItem = this.#state.activeItem;
    if (!activeItem) return;

    event.preventDefault();
    this.#deactivate(activeItem, true);
    activeItem.focus({ preventScroll: true });
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * When the menu is closed, reconcile pointer movement near the bottom edge
   * of the desktop navigation row. This catches upward approaches that can
   * miss the native pointerenter hit-test on some browsers/layout states.
   * @param {PointerEvent} event
   */
  #onClosedMenuPointerMove = (event) => {
    if (this.#state.activeItem) return;
    if (event.pointerType && event.pointerType !== 'mouse') return;
    if (!window.matchMedia('(min-width: 1100px)').matches) return;
    if (event.movementY >= 0) return;

    const menu = this.querySelector('.menu-list');
    if (!(menu instanceof HTMLElement)) return;

    const rect = menu.getBoundingClientRect();
    const activationBand = 16;

    if (event.clientX < rect.left || event.clientX > rect.right) return;
    if (event.clientY < rect.top - activationBand || event.clientY > rect.bottom + activationBand) return;

    const hitY = Math.min(Math.max(event.clientY, rect.top + 1), rect.bottom - 1);
    const target = document.elementFromPoint(event.clientX, hitY);
    if (!(target instanceof Element)) return;

    const listItem = target.closest('.menu-list__list-item');
    if (!(listItem instanceof HTMLElement)) return;
    if (listItem.getAttribute('aria-hidden') === 'true') return;

    const menuItem = findMenuItem(listItem);
    if (!menuItem || !findSubmenu(menuItem)) return;

    listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false, pointerType: 'mouse' }));
  };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;

      const activeSubmenu = findSubmenu(this.#state.activeItem);
      if (activeSubmenu?.contains(target)) {
        this.#clearActivationTimer();
        this.#clearDeactivationTimer();
        return;
      }

      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate a menu item. Pointer navigation uses hover-intent delays;
   * keyboard focus remains immediate.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    const item = findMenuItem(event.target);
    if (!item) return;

    this.#clearDeactivationTimer();

    if (item === this.#state.activeItem) {
      this.#clearActivationTimer();
      return;
    }

    const isDefaultSlot = event.target.slot === '';
    const submenu = findSubmenu(item) ?? (!isDefaultSlot ? this.overflowMenu : null);

    // Start fetching only the imagery the user is actually approaching.
    this.#preloadSubmenuImages(submenu);

    if (event instanceof FocusEvent) {
      this.#clearActivationTimer();
      this.#commitActivation(item, isDefaultSlot);
      return;
    }

    if (this.#pendingItem === item && this.#activationTimer) return;

    this.#clearActivationTimer();
    this.#pendingItem = item;

    const delay = this.#state.activeItem ? MENU_SWITCH_DELAY_MS : MENU_OPEN_DELAY_MS;
    this.#activationTimer = setTimeout(() => {
      this.#activationTimer = undefined;
      this.#pendingItem = null;
      this.#commitActivation(item, isDefaultSlot);
    }, delay);
  };

  /**
   * Commit the visual switch to a menu item after hover intent is confirmed.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   */
  #commitActivation(item, isDefaultSlot) {
    if (!this.headerComponent || item === this.#state.activeItem) return;
    
    const previouslyActiveItem = this.#state.activeItem;
    const previousSubmenu = findSubmenu(previouslyActiveItem);

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      if (previousSubmenu) delete previousSubmenu.dataset.active;
    }

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();
    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();

    if (submenu) {
      submenu.dataset.active = '';

      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = setTimeout(() => {
        this.#hoverDispatchTimer = undefined;
        if (this.#state.activeItem === item) {
          this.dispatchEvent(new MegaMenuHoverEvent());
        }
      }, HOVER_COMMIT_DELAY_MS);

      const syncHeight = () => {
        if (this.#state.activeItem !== item || !submenu) return;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (this.#state.activeItem !== item || !submenu) return;
            const finalHeight = this.#calculateSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
            this.#syncMenuHeight(finalHeight);
          });
        });
      };

      this.#submenuMutationObserver = new MutationObserver(syncHeight);
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      this.#submenuResizeObserver = new ResizeObserver(syncHeight);
      this.#submenuResizeObserver.observe(submenu);

    }

const syncInitialHeight = () => {
  if (this.#state.activeItem !== item) return;

  const finalHeight = submenu
    ? this.#calculateSubmenuHeight(submenu, isDefaultSlot, hasSubmenu)
    : 0;

  this.#syncMenuHeight(finalHeight);
};

// Prima misura subito, così il menu non appare vuoto
syncInitialHeight();

// Seconda misura dopo che il browser ha completato il layout reale
requestAnimationFrame(() => {
  requestAnimationFrame(() => {
    syncInitialHeight();
  });
});

this.style.setProperty('--submenu-opacity', '1');
this.#startPointerTracking(item, previouslyActiveItem);
  }

  /** Recalculate the currently open menu after viewport/layout changes. */
  #syncActiveMenuHeight() {
    const item = this.#state.activeItem;
    if (!item) return;

    const parentItem = item.parentElement;
    const isDefaultSlot = !(parentItem instanceof HTMLElement) || parentItem.slot !== 'overflow';
    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) submenu = this.overflowMenu;
    if (!submenu) return;

    this.#syncMenuHeight(this.#calculateSubmenuHeight(submenu, isDefaultSlot, hasSubmenu));
  }

  /**
   * Calculate the required open height for default and overflow menus.
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #calculateSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    let finalHeight = submenu.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      finalHeight = hasSubmenu
        ? Math.max(this.overflowMenu?.offsetHeight || 0, overflowListHeight)
        : overflowListHeight;
    }

    return finalHeight;
  }

  /**
   * Keep the submenu and header-underlay geometry in sync.
   * @param {number} submenuHeight
   */
  #syncMenuHeight(submenuHeight) {
    if (!this.headerComponent) return;
    const headerVisibleHeight = this.#getHeaderVisibleHeight();
    this.headerComponent.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.#setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight);
  }

  /**
   * Deactivate after a short grace period for pointer users.
   * Keyboard blur keeps the existing focus-aware behavior.
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const eventItem = findMenuItem(event.target);

    // Leaving a candidate item before its delayed activation should simply cancel it.
    if (eventItem && eventItem === this.#pendingItem) {
      this.#clearActivationTimer();
      if (eventItem !== this.#state.activeItem) return;
    }

    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node && event.type === 'blur' && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      this.#clearDeactivationTimer();
      return;
    }

    if (event instanceof FocusEvent) {
      this.#deactivate();
      return;
    }

    // If pointerleave belongs to an item that never became active, keep the current menu open.
    if (eventItem && this.#state.activeItem && eventItem !== this.#state.activeItem) return;

    this.#scheduleDeactivation();
  }

  #scheduleDeactivation() {
    if (!this.#state.activeItem) return;

    this.#clearDeactivationTimer();
    const itemAtScheduleTime = this.#state.activeItem;

    this.#deactivationTimer = setTimeout(() => {
      this.#deactivationTimer = undefined;
      if (this.#state.activeItem !== itemAtScheduleTime) return;

      const submenu = findSubmenu(itemAtScheduleTime);
      const stillHovered =
        itemAtScheduleTime.matches(':hover') ||
        itemAtScheduleTime.parentElement?.matches(':hover') ||
        submenu?.matches(':hover') ||
        this.overflowListHovered ||
        this.overflowMenu?.matches(':hover');

      if (stillHovered) return;
      this.#deactivate(itemAtScheduleTime);
    }, MENU_CLOSE_DELAY_MS);
  }

  #clearActivationTimer() {
    clearTimeout(this.#activationTimer);
    this.#activationTimer = undefined;
    this.#pendingItem = null;
  }

  #clearDeactivationTimer() {
    clearTimeout(this.#deactivationTimer);
    this.#deactivationTimer = undefined;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;

    // Normal pointer navigation keeps the overflow menu open while hovered.
    // External UI layers (cart/search) may force a clean close.
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#clearActivationTimer();
    this.#clearDeactivationTimer();
    clearTimeout(this.#viewportExitTimer);
    this.#viewportExitTimer = undefined;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    this.#cleanupMutationObserver();
    this.#cleanupResizeObserver();

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item);

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  /**
   * Force-closes any active mega menu.
   * Used by the global header UI coordinator when cart/search takes priority.
   */
  closeAll() {
    this.#clearActivationTimer();
    this.#clearDeactivationTimer();
    this.#deactivate(this.#state.activeItem, true);
  }

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload only images that belong to the submenu the user is approaching.
   * @param {HTMLElement | null} submenu
   */
  #preloadSubmenuImages(submenu) {
    if (!submenu) return;
    const images = submenu.querySelectorAll('img[loading="lazy"]');
    images.forEach((image) => image.removeAttribute('loading'));
  }

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }

  #cleanupResizeObserver() {
    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[ref="menuitem"]')) return /** @type {HTMLElement} */ (element);

  const listItem = element.closest('.menu-list__list-item');
  if (!(listItem instanceof HTMLElement)) return null;

  if (listItem.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item.
    return findMenuItem(listItem.parentElement?.querySelector('[slot="overflow"]'));
  }

  return /** @type {HTMLElement | null} */ (listItem.querySelector('[ref="menuitem"]'));
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
